<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="wizard-backdrop" @click.self="handleClose">
        <div class="wizard-panel">

          <!-- Header -->
          <div class="wizard-header">
            <div>
              <p class="wizard-step-label">Import CV</p>
              <h2 class="wizard-title">{{ stepTitle }}</h2>
            </div>
            <button @click="handleClose" class="wizard-close">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Step 1: Upload -->
          <div v-if="step === 'upload'" class="wizard-body">
            <p class="text-sm text-zinc-500 dark:text-zinc-400 mb-4 leading-relaxed">
              Upload an existing CV and we'll extract your experience, education, activities and skills.
              Pick what to keep, and we'll add it to your master profile.
            </p>
            <div
              @dragover.prevent
              @drop.prevent="e => { uploadedFile = e.dataTransfer.files[0] }"
              class="upload-zone"
              :class="{ 'upload-zone--filled': uploadedFile }"
            >
              <template v-if="!uploadedFile">
                <svg class="w-8 h-8 text-zinc-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p class="text-sm text-zinc-500 mb-1">Drop your CV here or <label for="import-cv-upload" class="text-primary cursor-pointer hover:opacity-70">browse</label></p>
                <p class="text-xs text-zinc-400">PDF, DOC, DOCX, JPG, PNG — max 5 MB · up to 5 pages</p>
                <input type="file" id="import-cv-upload" @change="e => { uploadedFile = e.target.files[0] }" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" class="hidden" />
              </template>
              <template v-else>
                <svg class="w-6 h-6 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-sm font-medium text-zinc-900 dark:text-white">{{ uploadedFile.name }}</p>
                <p class="text-xs text-zinc-400 mt-0.5">{{ (uploadedFile.size / 1024 / 1024).toFixed(2) }} MB</p>
                <button @click="uploadedFile = null" class="text-xs text-zinc-400 hover:text-zinc-600 mt-2">Choose different file</button>
              </template>
            </div>
            <p v-if="error" class="text-xs text-red-500 text-center mt-3">{{ error }}</p>
          </div>

          <!-- Step 2: Parsing -->
          <div v-else-if="step === 'parsing'" class="wizard-loading">
            <div class="w-5 h-5 border-2 border-zinc-100 border-t-primary rounded-full animate-spin"></div>
            <p class="text-sm font-medium text-zinc-900 dark:text-white">Analyzing your CV…</p>
            <p class="text-xs text-zinc-400">This usually takes 10–20 seconds</p>
          </div>

          <!-- Step 3: Selection -->
          <div v-else-if="step === 'selection'" class="wizard-body wizard-body--scroll">

            <!-- Personal info -->
            <div v-if="hasPersonalInfo" class="selection-section">
              <p class="selection-label">Personal Info</p>
              <div
                @click="importPersonal = !importPersonal"
                class="entry-card"
                :class="{ 'entry-card--selected': importPersonal }"
              >
                <div v-if="importPersonal" class="entry-card-check">
                  <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                </div>
                <p class="text-sm font-semibold text-zinc-900 dark:text-white truncate">{{ cvData.personal_info.full_name || 'Contact details' }}</p>
                <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ personalSummary }}</p>
                <p class="text-[11px] text-zinc-400 mt-1">Only fills blank fields — won't overwrite existing details</p>
              </div>
            </div>

            <!-- Education -->
            <div v-if="cvData.education_list?.length" class="selection-section">
              <p class="selection-label">Education</p>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(edu, i) in cvData.education_list"
                  :key="`edu-${i}`"
                  @click="toggle('education', i)"
                  class="entry-card"
                  :class="{ 'entry-card--selected': selectedEducation.has(i) }"
                >
                  <div v-if="selectedEducation.has(i)" class="entry-card-check">
                    <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate">{{ edu.program_name || edu.institution || 'Untitled' }}</p>
                  <p v-if="edu.institution" class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ edu.institution }}</p>
                  <p v-if="dateRange(edu)" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ dateRange(edu) }}</p>
                </div>
              </div>
            </div>

            <!-- Experience -->
            <div v-if="cvData.experience_list?.length" class="selection-section">
              <p class="selection-label">Experience</p>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(exp, i) in cvData.experience_list"
                  :key="`exp-${i}`"
                  @click="toggle('experience', i)"
                  class="entry-card"
                  :class="{ 'entry-card--selected': selectedExperience.has(i) }"
                >
                  <div v-if="selectedExperience.has(i)" class="entry-card-check">
                    <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate">{{ exp.job_title || exp.company || 'Untitled' }}</p>
                  <p v-if="exp.company" class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ exp.company }}</p>
                  <p v-if="dateRange(exp)" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ dateRange(exp) }}</p>
                </div>
              </div>
            </div>

            <!-- Activities -->
            <div v-if="cvData.activities_list?.length" class="selection-section">
              <p class="selection-label">Activities & Leadership</p>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(act, i) in cvData.activities_list"
                  :key="`act-${i}`"
                  @click="toggle('activities', i)"
                  class="entry-card"
                  :class="{ 'entry-card--selected': selectedActivities.has(i) }"
                >
                  <div v-if="selectedActivities.has(i)" class="entry-card-check">
                    <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate">{{ act.role || act.organization || 'Untitled' }}</p>
                  <p v-if="act.organization" class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ act.organization }}</p>
                  <p v-if="dateRange(act)" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ dateRange(act) }}</p>
                </div>
              </div>
            </div>

            <!-- Skills -->
            <div v-if="cvData.skill_categories_list?.length" class="selection-section">
              <p class="selection-label">Skills & Interests</p>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="(skill, i) in cvData.skill_categories_list"
                  :key="`skill-${i}`"
                  @click="toggle('skills', i)"
                  class="entry-card"
                  style="flex: 0 1 calc(50% - 0.25rem)"
                  :class="{ 'entry-card--selected': selectedSkills.has(i) }"
                >
                  <div v-if="selectedSkills.has(i)" class="entry-card-check">
                    <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white truncate">{{ skill.category_name || 'Skills' }}</p>
                  <p class="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2 mt-0.5">{{ skill.content }}</p>
                </div>
              </div>
            </div>

            <p v-if="isEmptyResult" class="text-sm text-zinc-400 text-center py-6">
              We couldn't find any entries in this CV. Try a different file.
            </p>
            <p v-if="error" class="text-xs text-red-500 text-center mt-2">{{ error }}</p>
          </div>

          <!-- Step 4: Done -->
          <div v-else-if="step === 'done'" class="wizard-loading">
            <div class="w-11 h-11 rounded-xl border border-zinc-100 dark:border-zinc-800 flex items-center justify-center">
              <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p class="text-sm font-medium text-zinc-900 dark:text-white">Added to your master profile</p>
            <p class="text-xs text-zinc-400">{{ doneSummary }}</p>
          </div>

          <!-- Footer -->
          <div class="wizard-footer">
            <button v-if="step === 'selection'" @click="resetToUpload" class="wiz-btn-secondary">← Back</button>
            <span v-else></span>

            <button v-if="step === 'upload'" @click="uploadAndParse" :disabled="!uploadedFile" class="wiz-btn-primary">
              Extract Data
            </button>
            <button v-else-if="step === 'selection'" @click="saveToProfile" :disabled="isSaving || selectedCount === 0" class="wiz-btn-primary">
              <span v-if="isSaving" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              {{ isSaving ? 'Saving…' : `Add ${selectedCount} to profile` }}
            </button>
            <div v-else-if="step === 'done'" class="flex gap-2">
              <button @click="handleClose" class="wiz-btn-secondary">Done</button>
              <router-link to="/profile" class="wiz-btn-primary" @click="handleClose">View profile →</router-link>
            </div>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { validateCvParserFile, parseCvParserError } from '../config/cvConfig.js'

export default {
  name: 'ImportCvModal',
  props: {
    isOpen: { type: Boolean, required: true }
  },
  emits: ['close', 'imported'],
  setup(props, { emit }) {
    const step = ref('upload')      // 'upload' | 'parsing' | 'selection' | 'done'
    const error = ref(null)
    const uploadedFile = ref(null)
    const isSaving = ref(false)

    const cvData = ref({})
    const importPersonal = ref(false)
    const selectedEducation = ref(new Set())
    const selectedExperience = ref(new Set())
    const selectedActivities = ref(new Set())
    const selectedSkills = ref(new Set())
    const lastCreated = ref({})

    const getCSRFToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const stepTitle = computed(() => ({
      upload: 'Upload your CV',
      parsing: 'Reading your CV',
      selection: 'Select what to keep',
      done: 'Imported'
    }[step.value] || ''))

    const hasPersonalInfo = computed(() => {
      const p = cvData.value.personal_info || {}
      return !!(p.full_name || p.email || p.phone || p.location)
    })
    const personalSummary = computed(() => {
      const p = cvData.value.personal_info || {}
      return [p.email, p.phone, p.location].filter(Boolean).join(' · ')
    })

    const isEmptyResult = computed(() =>
      !hasPersonalInfo.value &&
      !cvData.value.education_list?.length &&
      !cvData.value.experience_list?.length &&
      !cvData.value.activities_list?.length &&
      !cvData.value.skill_categories_list?.length
    )

    const selectedCount = computed(() =>
      (importPersonal.value ? 1 : 0) +
      selectedEducation.value.size +
      selectedExperience.value.size +
      selectedActivities.value.size +
      selectedSkills.value.size
    )

    const doneSummary = computed(() => {
      const c = lastCreated.value
      const parts = []
      if (c.experience) parts.push(`${c.experience} experience`)
      if (c.education) parts.push(`${c.education} education`)
      if (c.activities) parts.push(`${c.activities} ${c.activities === 1 ? 'activity' : 'activities'}`)
      if (c.skills) parts.push(`${c.skills} skill ${c.skills === 1 ? 'category' : 'categories'}`)
      return parts.length ? parts.join(', ') + ' added.' : 'Your profile is up to date.'
    })

    const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const fmt = (m, y) => [m ? MONTHS[m] : null, y].filter(Boolean).join(' ')
    const dateRange = (e) => {
      const start = fmt(e.start_month, e.start_year)
      const end = e.is_current ? 'Present' : fmt(e.end_month, e.end_year)
      if (!start && !end) return ''
      return [start, end].filter(Boolean).join(' – ')
    }

    const sectionRefs = {
      education: selectedEducation,
      experience: selectedExperience,
      activities: selectedActivities,
      skills: selectedSkills
    }
    const toggle = (section, i) => {
      const set = sectionRefs[section]
      const next = new Set(set.value)
      next.has(i) ? next.delete(i) : next.add(i)
      set.value = next
    }

    const reset = () => {
      step.value = 'upload'
      error.value = null
      uploadedFile.value = null
      isSaving.value = false
      cvData.value = {}
      importPersonal.value = false
      selectedEducation.value = new Set()
      selectedExperience.value = new Set()
      selectedActivities.value = new Set()
      selectedSkills.value = new Set()
      lastCreated.value = {}
    }

    const resetToUpload = () => { step.value = 'upload'; error.value = null }

    const handleClose = () => { reset(); emit('close') }
    watch(() => props.isOpen, (open) => { if (open) reset() })

    const uploadAndParse = async () => {
      if (!uploadedFile.value) return

      const fileError = validateCvParserFile(uploadedFile.value)
      if (fileError) {
        error.value = fileError
        return
      }

      step.value = 'parsing'
      error.value = null
      try {
        const formData = new FormData()
        formData.append('file', uploadedFile.value)
        const res = await fetch('/api/cv_parser/upload', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-CSRF-Token': getCSRFToken() },
          body: formData
        })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(parseCvParserError(res, data))

        cvData.value = data.cv_data || {}
        // Default: select everything we found
        importPersonal.value = hasPersonalInfo.value
        selectedEducation.value = new Set((cvData.value.education_list || []).map((_, i) => i))
        selectedExperience.value = new Set((cvData.value.experience_list || []).map((_, i) => i))
        selectedActivities.value = new Set((cvData.value.activities_list || []).map((_, i) => i))
        selectedSkills.value = new Set((cvData.value.skill_categories_list || []).map((_, i) => i))
        step.value = 'selection'
      } catch (err) {
        error.value = err.message || 'Please try a different file.'
        step.value = 'upload'
      }
    }

    const saveToProfile = async () => {
      if (selectedCount.value === 0) return
      isSaving.value = true
      error.value = null
      try {
        const profileData = {
          education_list: [...selectedEducation.value].map(i => cvData.value.education_list[i]),
          experience_list: [...selectedExperience.value].map(i => cvData.value.experience_list[i]),
          activities_list: [...selectedActivities.value].map(i => cvData.value.activities_list[i]),
          skill_categories: [...selectedSkills.value].map(i => ({
            name: cvData.value.skill_categories_list[i].category_name,
            content: cvData.value.skill_categories_list[i].content
          }))
        }
        if (importPersonal.value) profileData.personal_info = cvData.value.personal_info

        const res = await fetch('/api/users/import_to_profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': getCSRFToken() },
          body: JSON.stringify({ profile_data: profileData })
        })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(data.message || 'Failed to import to profile.')

        lastCreated.value = data.created || {}
        step.value = 'done'
        emit('imported')
      } catch (err) {
        error.value = err.message || 'Failed to import. Please try again.'
      } finally {
        isSaving.value = false
      }
    }

    return {
      step, error, uploadedFile, isSaving,
      cvData, importPersonal,
      selectedEducation, selectedExperience, selectedActivities, selectedSkills,
      stepTitle, hasPersonalInfo, personalSummary, isEmptyResult,
      selectedCount, doneSummary, dateRange, toggle,
      handleClose, resetToUpload, uploadAndParse, saveToProfile
    }
  }
}
</script>

<style scoped>
.wizard-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center p-4;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
}
.wizard-panel {
  @apply bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden;
  max-height: 90vh;
}
.wizard-header {
  @apply flex items-start justify-between px-6 pt-6 pb-5 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.wizard-step-label {
  @apply text-xs font-semibold uppercase tracking-widest text-gold dark:text-gold-400 mb-1;
}
.wizard-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-white;
}
.wizard-close {
  @apply p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}
.wizard-loading {
  @apply flex flex-col items-center justify-center gap-3 py-12 px-6;
}
.wizard-body {
  @apply px-6 py-5 flex-1;
}
.wizard-body--scroll {
  @apply overflow-y-auto;
}
.wizard-footer {
  @apply flex items-center justify-between px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.wiz-btn-primary {
  @apply inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity;
}
.wiz-btn-secondary {
  @apply inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors;
}
.upload-zone {
  @apply border border-dashed border-zinc-200 dark:border-zinc-700 rounded-xl p-10 flex flex-col items-center text-center hover:border-primary dark:hover:border-primary-400 transition-colors;
}
.upload-zone--filled {
  @apply border-solid border-primary dark:border-primary-400;
}
.selection-section {
  @apply mb-5;
}
.selection-label {
  @apply text-xs font-semibold uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2;
}
.entry-card {
  @apply relative border border-zinc-100 dark:border-zinc-800 rounded-xl p-3 cursor-pointer transition-colors hover:border-zinc-300 dark:hover:border-zinc-600;
}
.entry-card--selected {
  @apply border-gold dark:border-gold-500 bg-gold-50/40 dark:bg-gold-900/10;
}
.entry-card-check {
  @apply absolute top-2 right-2 w-4 h-4 rounded-full bg-gold flex items-center justify-center flex-shrink-0;
}
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
</style>
